import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Plus, 
  Filter,
  MoreVertical,
  MessageSquare,
  Star,
  Archive,
  AlertCircle,
  CheckCircle,
  Clock,
  TrendingUp,
  Users,
  Bot,
  Sparkles,
  Phone,
  Mail,
  Wifi,
  WifiOff,
  Send,
  Paperclip,
  Smile,
  User,
  Upload,
  FileText,
  Loader2,
  Brain,
  Download,
  Building2,
  ChevronDown,
  ChevronUp,
  UserCheck,
  UserX,
  Settings,
  Calendar,
  Clock3,
  AlertTriangle,
  X,
  Check,
  CheckCheck,
  ImageIcon,
  VolumeX,
  VideoIcon,
  RefreshCw,
  Info
} from 'lucide-react';

// Import AI Analysis Sidebar with enhanced capabilities
import { AIAnalysisSidebar, AIAnalysisResult, AIResponseGeneration } from '../../components/conversations/AIAnalysisSidebar';

// Import enhanced avatar components
import { SmartAvatar, GroupAvatar, CompanyAvatar } from '../../components/ui/SmartAvatar';
import { useContactPhotos, ContactInfo } from '../../hooks/useContactPhotos';

// Import User Profile Modal
import { UserProfileModal, User as UserProfile } from './components/UserProfileModal';

// Import Message Info Modal
import { MessageInfoModal, MessageInfo } from './components/MessageInfoModal';

// Import Automation Modal
import { AutomationModal, AutomationConfig } from './components/AutomationModal';

// Import Automation Indicators
import { AutomationDot, AutomationBadge } from './components/AutomationIndicator';

// Import Queued Response Components
import { useQueuedResponses } from './hooks/useQueuedResponses';
import { QueuedResponseBubble } from './components/QueuedResponseBubble';

// Import Automation Service
import { automationService } from '../../services/automationService';

// Import Real-time Conversations Hook
import { useRealTimeConversations } from './hooks/useRealTimeConversations';

// Import design system components (keeping existing UI components for now)
// Will gradually migrate to design system components

// UI Components
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/Input/Input';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card/Card';
import { Separator } from '../../components/ui/separator';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Skeleton } from '../../components/ui/skeleton';
import { Textarea } from '../../components/ui/textarea';
import { AutoExpandingTextarea } from '../../components/ui/AutoExpandingTextarea';
import { cn } from '../../utils/cn';
import { autoSetupDemoAuth } from '../../utils/demoAuth';

// Types
interface Contact {
  id: string;
  name: string;
  email: string;
  company?: string;
  avatar?: string;
}

interface Message {
  id: string;
  content: string;
  timestamp: string;
  isOutgoing: boolean;
  channel: 'email' | 'sms' | 'phone';
  status: 'sent' | 'delivered' | 'read' | 'failed';
  attachments?: File[];
  metadata?: any;
  // Enhanced sender information
  sender?: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
    type: 'contact' | 'user' | 'ai';
    // Additional info for AI messages
    aiModel?: string;
    // Workflow information
    workflowExecutionId?: string;
    workflowName?: string;
  };
}

interface Target {
  id: string;
  name: string;
  domain?: string;
  industry?: string;
  companySize?: string;
  status: string;
  priority: string;
  contactCount?: number;
  campaignCount?: number;
}

interface Conversation {
  id: string;
  contact: Contact;
  lastMessage: Message;
  status: 'active' | 'positive' | 'priority' | 'needs-response' | 'resolved';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  campaign?: string;
  target?: Target; // Add target information for target-based threading
  messageCount: number;
  unreadCount: number;
  labels: string[];
  aiAnalysis?: {
    sentiment: 'positive' | 'neutral' | 'negative';
    intent: string;
    confidence: number;
  };
  isStarred: boolean;
  updatedAt: string;
}

interface ConversationStats {
  totalConversations: number;
  activeConversations: number;
  avgResponseTime: string;
  responseRate: number;
}

// Status configuration with improved colors and dark mode support
const statusConfig = {
  active: { 
    label: 'Active', 
    className: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800',
    dotClass: 'bg-green-500'
  },
  positive: { 
    label: 'Positive', 
    className: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800',
    dotClass: 'bg-blue-500'
  },
  priority: { 
    label: 'Priority', 
    className: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800',
    dotClass: 'bg-red-500'
  },
  'needs-response': { 
    label: 'Needs Response', 
    className: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-300 dark:border-orange-800',
    dotClass: 'bg-orange-500'
  },
  resolved: { 
    label: 'Resolved', 
    className: 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-950 dark:text-gray-300 dark:border-gray-800',
    dotClass: 'bg-gray-500'
  }
};

// Improved Avatar Component with online indicator
const Avatar: React.FC<{
  src?: string;
  fallback: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  online?: boolean;
}> = ({ src, fallback, className, size = 'md', online }) => {
  const sizeClasses = {
    sm: 'h-8 w-8 text-xs',
    md: 'h-10 w-10 text-sm',
    lg: 'h-12 w-12 text-base'
  };

  return (
    <div className="relative">
      <div className={cn(
        'rounded-full bg-gradient-to-br from-blue-100 to-purple-100 text-blue-700 font-semibold flex items-center justify-center overflow-hidden',
        'dark:from-blue-900 dark:to-purple-900 dark:text-blue-300',
        sizeClasses[size],
        className
      )}>
        {src ? (
          <img src={src} alt="" className="rounded-full object-cover w-full h-full" />
        ) : (
          fallback
        )}
      </div>
      {online !== undefined && (
        <div className={cn(
          "absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-900",
          online ? "bg-green-500" : "bg-gray-400"
        )} />
      )}
    </div>
  );
};

// Enhanced Metric Card with better styling
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  change?: string;
  icon: React.ComponentType<{ className?: string }>;
  trend?: 'up' | 'down' | 'neutral';
}> = ({ title, value, change, icon: Icon, trend = 'neutral' }) => {
  const trendColors = {
    up: 'text-green-600 dark:text-green-400',
    down: 'text-red-600 dark:text-red-400',
    neutral: 'text-gray-600 dark:text-gray-400'
  };

  return (
    <Card className="p-3 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">{title}</p>
          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{value}</p>
          {change && (
            <div className="flex items-center gap-1">
              <TrendingUp className={cn("h-3 w-3", trendColors[trend])} />
              <span className={cn("text-xs font-medium", trendColors[trend])}>
                {change}
              </span>
            </div>
          )}
        </div>
        <div className="p-2 bg-blue-50 dark:bg-blue-950 rounded-lg">
          <Icon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </div>
      </div>
    </Card>
  );
};

// Real-time Connection Status Component with enhanced UX
const ConnectionStatus: React.FC<{ isConnected: boolean; connectionStatus?: any }> = ({ 
  isConnected, 
  connectionStatus 
}) => {
  if (isConnected && !connectionStatus?.reconnecting) return null;

  const isReconnecting = connectionStatus?.reconnecting;
  const attempts = connectionStatus?.reconnectAttempts || 0;
  const latency = connectionStatus?.lastPing;

  return (
    <Alert className={cn(
      "mb-4",
      isReconnecting 
        ? "border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950"
        : "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950"
    )}>
      {isReconnecting ? (
        <RefreshCw className="h-4 w-4 text-yellow-600 dark:text-yellow-400 animate-spin" />
      ) : (
        <WifiOff className="h-4 w-4 text-red-600 dark:text-red-400" />
      )}
      <AlertDescription className={cn(
        "text-sm",
        isReconnecting 
          ? "text-yellow-700 dark:text-yellow-300"
          : "text-red-700 dark:text-red-300"
      )}>
        {isReconnecting ? (
          <>
            Reconnecting to real-time updates...
            {attempts > 0 && ` (attempt ${attempts})`}
          </>
        ) : (
          <>
            Real-time updates disconnected. 
            {latency && ` Last ping: ${latency}ms`}
          </>
        )}
      </AlertDescription>
    </Alert>
  );
};

// Enhanced Conversation List Item with better accessibility
const ConversationListItem: React.FC<{
  conversation: Conversation;
  isSelected: boolean;
  onClick: () => void;
}> = ({ conversation, isSelected, onClick }) => {
  const status = statusConfig[conversation.status];
  const initials = conversation.contact.name
    .split(' ')
    .map(n => n.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();

  const timeAgo = useMemo(() => {
    const now = new Date();
    const updated = new Date(conversation.updatedAt);
    const diffInMinutes = Math.floor((now.getTime() - updated.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return updated.toLocaleDateString();
  }, [conversation.updatedAt]);

  return (
    <Card 
      className={cn(
        "cursor-pointer transition-all duration-200 hover:shadow-md hover:border-blue-200 dark:hover:border-blue-800",
        "focus-within:ring-2 focus-within:ring-blue-100 dark:focus-within:ring-blue-900",
        isSelected && "ring-2 ring-blue-200 bg-blue-50 border-blue-200 dark:ring-blue-800 dark:bg-blue-950 dark:border-blue-800"
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <CardContent className="p-4">
        <div className="flex gap-3">
          <div className="relative flex-shrink-0">
            <SmartAvatar
              contact={{
                email: conversation.contact.email,
                name: conversation.contact.name,
                company: conversation.contact.company
              }}
              size="md"
              showOnlineIndicator={true}
              isOnline={Math.random() > 0.5} // Simulate online status - replace with real data
              enablePhotoEnrichment={true}
              showCompanyLogo={true}
            />
            <div className={cn(
              "absolute -bottom-1 -right-1 h-3 w-3 rounded-full border-2 border-white dark:border-gray-900",
              status.dotClass
            )} />
          </div>

          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                  {conversation.contact.name}
                </h4>
                {conversation.isStarred && (
                  <Star className="h-3 w-3 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                )}
              </div>
              <time className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                {timeAgo}
              </time>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={cn("text-xs border", status.className)}>
                {status.label}
              </Badge>
              {/* Automation Indicator */}
              <AutomationDot 
                conversationId={conversation.id}
                className="opacity-80 hover:opacity-100 transition-opacity"
              />
              {conversation.target && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Badge className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                        <Building2 className="h-2 w-2 mr-1" />
                        {conversation.target.name}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent className="text-xs">
                      <div className="space-y-1">
                        <div><strong>Target:</strong> {conversation.target.name}</div>
                        {conversation.target.domain && <div><strong>Domain:</strong> {conversation.target.domain}</div>}
                        {conversation.target.industry && <div><strong>Industry:</strong> {conversation.target.industry}</div>}
                        {conversation.target.companySize && <div><strong>Size:</strong> {conversation.target.companySize}</div>}
                        {conversation.target.contactCount && <div><strong>Contacts:</strong> {conversation.target.contactCount}</div>}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {conversation.campaign && (
                <Badge className="text-xs bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                  {conversation.campaign}
                </Badge>
              )}
              {conversation.aiAnalysis && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Badge className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                        <Sparkles className="h-2 w-2 mr-1" />
                        {Math.round(conversation.aiAnalysis.confidence * 100)}%
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent className="text-xs">
                      <div>Sentiment: {conversation.aiAnalysis.sentiment}</div>
                      <div>Intent: {conversation.aiAnalysis.intent}</div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 leading-5">
              {conversation.lastMessage.content || 'No messages yet'}
            </p>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <MessageSquare className="h-3 w-3" />
                <span>{conversation.messageCount} message{conversation.messageCount !== 1 ? 's' : ''}</span>
              </div>
              {conversation.unreadCount > 0 && (
                <Badge className="h-5 min-w-[20px] px-1 flex items-center justify-center text-xs bg-blue-600 text-white">
                  {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Enhanced Message Bubble with attachment support
const MessageBubble: React.FC<{ 
  message: Message; 
  contact?: Contact;
  isLast?: boolean;
  onUserClick?: (user: UserProfile) => void;
  onInfoClick?: (messageInfo: MessageInfo) => void;
}> = ({ message, contact, isLast = false, onUserClick, onInfoClick }) => {
  const isOutgoing = message.isOutgoing;
  const [imageError, setImageError] = useState(false);

  const getFileIcon = (filename: string) => {
    const ext = filename.toLowerCase().split('.').pop();
    switch (ext) {
      case 'pdf': return <FileText className="h-4 w-4 text-red-500" />;
      case 'doc':
      case 'docx': return <FileText className="h-4 w-4 text-blue-500" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return <ImageIcon className="h-4 w-4 text-green-500" />;
      case 'mp3':
      case 'wav': return <VolumeX className="h-4 w-4 text-purple-500" />;
      case 'mp4':
      case 'avi': return <VideoIcon className="h-4 w-4 text-orange-500" />;
      default: return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusIcon = () => {
    switch (message.status) {
      case 'sent':
        return <Check className="h-3 w-3 text-gray-400" />;
      case 'delivered':
        return <CheckCheck className="h-3 w-3 text-blue-500" />;
      case 'read':
        return <CheckCheck className="h-3 w-3 text-green-500" />;
      case 'failed':
        return <AlertTriangle className="h-3 w-3 text-red-500" />;
      default:
        return null;
    }
  };

  // Determine sender information
  const sender = message.sender || {
    id: isOutgoing ? 'current-user' : contact?.id || 'unknown',
    name: isOutgoing ? 'You' : contact?.name || 'Unknown',
    email: isOutgoing ? 'you@company.com' : contact?.email || 'unknown@email.com',
    type: isOutgoing ? 'user' as const : 'contact' as const,
    avatar: isOutgoing ? undefined : contact?.avatar
  };

  const handleUserClick = () => {
    if (onUserClick && sender) {
      // Convert sender to UserProfile format
      const userProfile: UserProfile = {
        id: sender.id,
        name: sender.name,
        email: sender.email,
        avatar: sender.avatar,
        type: sender.type,
        company: contact?.company,
        isOnline: Math.random() > 0.5, // Simulate online status
        // Mock additional data - in real app, this would come from API
        stats: {
          totalMessages: Math.floor(Math.random() * 100) + 10,
          responseTime: '2.3h',
          lastMessageAt: message.timestamp,
          conversationsCount: Math.floor(Math.random() * 20) + 1
        },
        ...(sender.type === 'contact' && {
          customerRelationship: {
            isCustomer: true,
            customerSince: '2023-01-15',
            accountType: 'premium' as const,
            lifetimeValue: Math.floor(Math.random() * 50000) + 1000
          }
        }),
        ...(sender.type === 'user' && {
          teamRelationship: {
            isTeamMember: true,
            role: 'agent' as const,
            department: 'Customer Success',
            joinedAt: '2023-01-01'
          }
        })
      };
      onUserClick(userProfile);
    }
  };

  const handleInfoClick = () => {
    if (onInfoClick) {
      // Create comprehensive message info - in real app, this would come from API
      const messageInfo: MessageInfo = {
        id: message.id,
        timestamp: message.timestamp,
        channel: message.channel,
        status: message.status,
        source: sender.type === 'ai' ? 'ai' : 
                message.sender?.workflowExecutionId ? 'workflow' : 
                'human',
        // Mock AI info for AI messages
        ...(sender.type === 'ai' && {
          aiInfo: {
            model: 'gpt-4-turbo',
            prompt: `Analyze this customer message and provide a helpful response: "${message.content}"`,
            systemPrompt: 'You are a helpful customer service AI assistant. Respond professionally and empathetically.',
            thoughtProcess: 'The customer seems to have a question about our product. I should provide a clear, helpful response while maintaining a professional tone.',
            confidence: 0.85,
            tokensUsed: 150,
            processingTime: 2340,
            temperature: 0.7,
            maxTokens: 500,
            fullInput: `Context: Customer inquiry\nMessage: ${message.content}`,
            fullOutput: message.content,
            reasoning: [
              'Analyzed customer sentiment as neutral-positive',
              'Identified main concern about product features',
              'Selected appropriate tone for response',
              'Generated solution-focused reply'
            ]
          }
        }),
        // Mock workflow info for workflow messages
        ...(message.sender?.workflowExecutionId && {
          workflowInfo: {
            executionId: message.sender.workflowExecutionId,
            workflowName: message.sender.workflowName || 'Customer Follow-up',
            workflowVersion: '1.2.0',
            triggerType: 'automatic' as const,
            executionTime: 1250,
            steps: [
              {
                name: 'Trigger Evaluation',
                status: 'success' as const,
                duration: 150,
                output: { condition: 'Customer inactive for 3 days' }
              },
              {
                name: 'Template Selection',
                status: 'success' as const,
                duration: 200,
                output: { template: 'follow-up-v2' }
              },
              {
                name: 'Message Generation',
                status: 'success' as const,
                duration: 800,
                output: { messageLength: message.content.length }
              },
              {
                name: 'Delivery',
                status: 'success' as const,
                duration: 100
              }
            ],
            variables: {
              customerName: contact?.name || 'Customer',
              lastActivity: '3 days ago',
              accountType: 'premium'
            }
          }
        }),
        // Message metadata
        metadata: {
          subject: message.channel === 'email' ? 'Re: Your inquiry' : undefined,
          attachmentCount: message.attachments?.length || 0,
          editedByHuman: Math.random() > 0.8, // Simulate some human editing
          deliveryAttempts: message.status === 'failed' ? 2 : 1
        },
        // Performance metrics
        performance: {
          loadTime: Math.floor(Math.random() * 100) + 50,
          renderTime: Math.floor(Math.random() * 20) + 10,
          networkLatency: Math.floor(Math.random() * 200) + 50
        }
      };
      onInfoClick(messageInfo);
    }
  };

  return (
    <div className={cn(
      "flex items-start gap-3 group",
      isOutgoing ? "justify-end" : "justify-start"
    )}>
      {/* Sender Avatar - Always show for better UX */}
      <div 
        className="cursor-pointer hover:scale-105 transition-transform"
        onClick={handleUserClick}
      >
        <Avatar
          src={sender.avatar}
          fallback={sender.name.charAt(0).toUpperCase()}
          size="sm"
          online={sender.type === 'user' && Math.random() > 0.5} // Mock online status for team members
        />
      </div>
      
      <div className={cn(
        "max-w-[70%] space-y-1",
        isOutgoing ? "text-right" : "text-left"
      )}>
        {/* Sender Info - Show when different from previous message or when first message */}
        <div className={cn(
          "flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1",
          isOutgoing ? "justify-end" : "justify-start"
        )}>
          <span 
            className="font-medium cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            onClick={handleUserClick}
          >
            {sender.name}
          </span>
          {sender.type === 'ai' && (
            <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 text-xs px-1 py-0">
              AI
            </Badge>
          )}
          {message.sender?.workflowName && (
            <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 text-xs px-1 py-0">
              Workflow
            </Badge>
          )}
        </div>
        
        <div className={cn(
          "inline-block px-4 py-2 rounded-2xl break-words",
          isOutgoing
            ? message.status === 'failed' 
              ? "bg-red-100 text-red-800 border border-red-300 dark:bg-red-950 dark:text-red-200 dark:border-red-800"
              : "bg-blue-600 text-white"
            : "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-600"
      )}>
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        
          {/* Show error details for failed messages */}
          {message.status === 'failed' && message.metadata?.error && (
            <div className="mt-2 pt-2 border-t border-red-200 dark:border-red-700">
              <p className="text-xs text-red-600 dark:text-red-400">
                <AlertTriangle className="h-3 w-3 inline mr-1" />
                Failed to send: {message.metadata.error}
              </p>
                      </div>
                    )}
          
          {/* Attachments */}
          {message.attachments && message.attachments.length > 0 && (
            <div className="mt-2 space-y-1">
              {message.attachments.map((file, index) => (
                <div key={index} className="flex items-center gap-2 text-xs">
                  {getFileIcon(file.name)}
                  <span className={cn(
                    isOutgoing ? "text-blue-100" : "text-gray-600 dark:text-gray-400"
                  )}>
                    {file.name}
                  </span>
                  </div>
              ))}
          </div>
        )}
        </div>
        
        <div className={cn(
          "flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400",
          isOutgoing ? "justify-end" : "justify-start"
          )}>
          <span>{new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          {isOutgoing && getStatusIcon()}
          
          {/* Message Info Button */}
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity",
              "hover:bg-gray-100 dark:hover:bg-gray-700"
            )}
            onClick={handleInfoClick}
            title="Message Information"
          >
            <Info className="h-3 w-3" />
          </Button>
          </div>
        </div>
    </div>
  );
};

// Add new interface for team user
interface TeamUser {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  isOnline: boolean;
  role: 'admin' | 'agent' | 'viewer';
}

// Add new filter states
interface ConversationFilters {
  status: string;
  assignedTo: string; // 'all' | 'ai' | 'unassigned' | userId
  priority: string;
  channel: string;
  dateRange: string;
}

// Enhanced Filter Panel Component
const FilterPanel: React.FC<{
  filters: ConversationFilters;
  onFiltersChange: (filters: ConversationFilters) => void;
  teamUsers: TeamUser[];
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ filters, onFiltersChange, teamUsers, isExpanded, onToggle }) => {
  return (
    <div className="border-b border-gray-200 dark:border-gray-700">
      <Button
        variant="ghost"
        onClick={onToggle}
        className="w-full justify-between p-3 h-auto hover:bg-gray-50 dark:hover:bg-gray-800"
      >
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4" />
          <span className="font-medium">Advanced Filters</span>
        </div>
        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </Button>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-4 space-y-4 bg-gray-50 dark:bg-gray-900/50">
              {/* Assignment Filter */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Assignment
                </label>
                <Select 
                  value={filters.assignedTo} 
                  onValueChange={(value) => onFiltersChange({ ...filters, assignedTo: value })}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="All conversations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <div className="flex items-center gap-2">
                        <Users className="h-3 w-3" />
                        All Conversations
                      </div>
                    </SelectItem>
                    <SelectItem value="ai">
                      <div className="flex items-center gap-2">
                        <Bot className="h-3 w-3 text-purple-500" />
                        AI Assigned
                      </div>
                    </SelectItem>
                    <SelectItem value="unassigned">
                      <div className="flex items-center gap-2">
                        <UserX className="h-3 w-3 text-gray-500" />
                        Unassigned
                      </div>
                    </SelectItem>
                    <Separator />
                    {teamUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <Avatar
                              src={user.avatar}
                              fallback={user.name.split(' ').map(n => n[0]).join('')}
                              size="sm"
                              className="h-4 w-4"
                            />
                            <div className={cn(
                              "absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-white",
                              user.isOnline ? "bg-green-500" : "bg-gray-400"
                            )} />
                          </div>
                          <span className="truncate">{user.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Status Filter */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Status
                </label>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <Button
                      key={key}
                      variant={filters.status === key ? "default" : "outline"}
                      size="sm"
                      onClick={() => onFiltersChange({ 
                        ...filters, 
                        status: filters.status === key ? 'all' : key 
                      })}
                      className={cn(
                        "h-6 px-2 text-xs",
                        filters.status === key ? "bg-blue-600 text-white" : ""
                      )}
                    >
                      <div className={cn("w-2 h-2 rounded-full mr-1", config.dotClass)} />
                      {config.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Priority Filter */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Priority
                </label>
                <div className="flex gap-1">
                  {['urgent', 'high', 'medium', 'low'].map((priority) => (
                    <Button
                      key={priority}
                      variant={filters.priority === priority ? "default" : "outline"}
                      size="sm"
                      onClick={() => onFiltersChange({ 
                        ...filters, 
                        priority: filters.priority === priority ? 'all' : priority 
                      })}
                      className={cn(
                        "h-6 px-2 text-xs capitalize",
                        filters.priority === priority ? "bg-blue-600 text-white" : ""
                      )}
                    >
                      {priority}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Channel Filter */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Channel
                </label>
                <div className="flex gap-1">
                  {[
                    { key: 'email', label: 'Email', icon: Mail },
                    { key: 'sms', label: 'SMS', icon: MessageSquare },
                    { key: 'phone', label: 'Phone', icon: Phone }
                  ].map(({ key, label, icon: Icon }) => (
                    <Button
                      key={key}
                      variant={filters.channel === key ? "default" : "outline"}
                      size="sm"
                      onClick={() => onFiltersChange({ 
                        ...filters, 
                        channel: filters.channel === key ? 'all' : key 
                      })}
                      className={cn(
                        "h-6 px-2 text-xs",
                        filters.channel === key ? "bg-blue-600 text-white" : ""
                      )}
                    >
                      <Icon className="h-3 w-3 mr-1" />
                      {label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Date Range Filter */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Date Range
                </label>
                <Select 
                  value={filters.dateRange} 
                  onValueChange={(value) => onFiltersChange({ ...filters, dateRange: value })}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="All time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All time</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="yesterday">Yesterday</SelectItem>
                    <SelectItem value="week">This week</SelectItem>
                    <SelectItem value="month">This month</SelectItem>
                    <SelectItem value="quarter">This quarter</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Clear Filters */}
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onFiltersChange({
                    status: 'all',
                    assignedTo: 'all',
                    priority: 'all',
                    channel: 'all',
                    dateRange: 'all'
                  })}
                  className="w-full h-7 text-xs text-gray-600 dark:text-gray-400"
                >
                  Clear all filters
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Main Component
export const ImprovedConversationsPage: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [messageSendError, setMessageSendError] = useState<{
    message: string;
    details: string;
    timestamp: Date;
  } | null>(null);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  
  // Ref for auto-scrolling to bottom of messages
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Enhanced filtering state
  const [filters, setFilters] = useState<ConversationFilters>({
    status: 'all',
    assignedTo: 'all',
    priority: 'all',
    channel: 'all',
    dateRange: 'all'
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Team users - mock data for now, "Coming soon" for real data
  const [teamUsers] = useState<TeamUser[]>([
    {
      id: '64d45fe8-d8a3-4a2e-99a7-657ce58f6bb7',
      name: 'Admin User',
      email: 'admin@trybombard.com',
      isOnline: true,
      role: 'admin'
    }
    // Additional team users will be loaded from API - Coming soon
  ]);
  
  // AI Analysis Sidebar state
  const [showAIAnalysis, setShowAIAnalysis] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState<AIAnalysisResult | null>(null);

  // User Profile Modal state
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);

  // Message Info Modal state
  const [showMessageInfo, setShowMessageInfo] = useState(false);
  const [selectedMessageInfo, setSelectedMessageInfo] = useState<MessageInfo | null>(null);

  // Automation Modal state
  const [showAutomation, setShowAutomation] = useState(false);
  const [automationConfig, setAutomationConfig] = useState<AutomationConfig | null>(null);

  // Queued responses hook
  const { queuedResponses, loading: queuedLoading, error: queuedError, refetch: refetchQueued } = useQueuedResponses(selectedConversation?.id || null);





  // Debug logging
  console.log('🔍 ImprovedConversationsPage render:', {
    loading,
    conversationsCount: conversations.length,
    selectedConversation: selectedConversation?.id,
    error
  });

  // Real messages loading from database using revolutionary conversation service
  // NOTE: This function ONLY loads messages for viewing - it does NOT update conversation ordering
  // Conversation ordering should only change when sending new messages or refreshing from backend
  const loadMessages = useCallback(async (conversationId: string) => {
    console.log('🔄 Loading messages from database for conversation (view only):', conversationId);
    
    try {
      // Try to get conversation details with messages using the main conversation service
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/conversations/${conversationId}/messages`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Revolutionary conversation data loaded:', data);
        
        if (data.success && data.data && data.data.messages) {
          // Map the real API response to our message interface
          const messages: Message[] = data.data.messages.map((msg: any) => ({
            id: msg.id,
            content: msg.content || 'No content',
            timestamp: msg.timestamp || msg.created_at || new Date().toISOString(),
            isOutgoing: msg.direction === 'outbound' || msg.isOutgoing,
            channel: msg.channel || 'email',
            status: msg.status || 'read',
            attachments: msg.attachments || [],
            metadata: msg.metadata || {}
          }));
          
          setMessages(messages);
          console.log('✅ Messages loaded from database:', messages.length);
          
          // Don't update conversation list when just viewing - only when sending new messages
          
          return;
        }
      }
      
      // Fallback to regular conversation service if revolutionary endpoint is not available
      const { conversationService } = await import('../../services/conversationService');
      const messageResult = await conversationService.getConversationMessages(conversationId);
      
      if (messageResult && messageResult.messages) {
        const messages: Message[] = messageResult.messages.map((msg: any) => ({
          id: msg.id,
          content: msg.content || msg.text || 'No content',
          timestamp: msg.timestamp || msg.created_at || new Date().toISOString(),
          isOutgoing: msg.direction === 'outbound' || msg.isOutgoing || msg.from_email !== selectedConversation?.contact.email,
          channel: msg.channel || 'email',
          status: msg.status || 'read',
          attachments: msg.attachments || [],
          metadata: msg.metadata || {}
        }));
        
        setMessages(messages);
        console.log('✅ Messages loaded via fallback service:', messages.length);
        
        // Don't update conversation list when just viewing - only when sending new messages
        
        return;
      }
      
      // If no messages found, set empty array
      console.log('📭 No messages found for conversation');
      setMessages([]);
      
    } catch (error) {
      console.error('❌ Failed to load messages from database:', error);
      // Set empty array on error instead of demo data
      setMessages([]);
    }
  }, [selectedConversation]);

  // Real message sending with email and file upload support
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || sendingMessage) return;
    
    setSendingMessage(true);
    setMessageSendError(null); // Clear any previous errors
    console.log('📤 Sending message via revolutionary conversation API...');
    
    try {
      // Create FormData for file uploads
      const formData = new FormData();
      formData.append('content', newMessage);
      formData.append('subject', `Re: ${selectedConversation.contact.name}`);
      formData.append('ai_generated', 'false');
      
      // Add attachments if any
      attachments.forEach((file) => {
        formData.append('attachments', file);
      });
      
      // Send message using the revolutionary conversation API
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/conversations/revolutionary/${selectedConversation.id}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Message sent successfully via revolutionary API:', result);
      
      if (result.success) {
        // Create optimistic message for immediate UI update
        const optimisticMessage: Message = {
          id: result.data?.id || Date.now().toString(),
          content: newMessage,
          timestamp: new Date().toISOString(),
          isOutgoing: true,
          channel: 'email',
          status: 'sent',
          attachments: attachments,
          metadata: {
            sendGridMessageId: result.data?.sendGridMessageId,
            deliveryStatus: result.data?.deliveryStatus,
            traceId: result.traceId
          }
        };
        
        setMessages(prev => [...prev, optimisticMessage]);
        setNewMessage('');
        setAttachments([]);
        
        // Immediately update the conversation's lastMessage info to trigger reordering
        // This is correct - we're adding a new message, so it should move to top
        setConversations(prev => {
          const updated = prev.map(conv => 
            conv.id === selectedConversation.id 
              ? {
                  ...conv,
                  lastMessage: optimisticMessage,
                  messageCount: conv.messageCount + 1,
                  updatedAt: optimisticMessage.timestamp
                }
              : conv
          );
          console.log('✅ Updated conversation after sending message (should move to top):', {
            conversationId: selectedConversation.id,
            newTimestamp: optimisticMessage.timestamp,
            contact: selectedConversation.contact.name
          });
          return updated;
        });
        
        // Show success feedback
        console.log('✅ Message sent and UI updated');
        
        // Update message status to delivered after a delay
        setTimeout(() => {
          setMessages(prev => 
            prev.map(msg => 
              msg.id === optimisticMessage.id 
                ? { ...msg, status: 'delivered' as const }
                : msg
            )
          );
          
          // Also update the conversation's lastMessage status
          setConversations(prev => 
            prev.map(conv => 
              conv.id === selectedConversation.id 
                ? {
                    ...conv,
                    lastMessage: { ...conv.lastMessage, status: 'delivered' as const }
                  }
                : conv
            )
          );
        }, 2000);

        // Update to read status after another delay
        setTimeout(() => {
          setMessages(prev => 
            prev.map(msg => 
              msg.id === optimisticMessage.id 
                ? { ...msg, status: 'read' as const }
                : msg
            )
          );
          
          // Also update the conversation's lastMessage status
          setConversations(prev => 
            prev.map(conv => 
              conv.id === selectedConversation.id 
                ? {
                    ...conv,
                    lastMessage: { ...conv.lastMessage, status: 'read' as const }
                  }
                : conv
            )
          );
        }, 5000);
        
        // Optional: Reload conversation to sync with server data (but UI is already updated)
        setTimeout(() => {
          loadMessages(selectedConversation.id);
          loadConversations(); // Refresh conversation list to sync with server
        }, 8000); // Longer delay since UI is already updated
        
      } else {
        throw new Error(result.error || 'Failed to send message');
      }
      
    } catch (error) {
      console.error('❌ Failed to send message:', error);
      
      // Set detailed message send error
      setMessageSendError({
        message: 'Failed to send message',
        details: error instanceof Error ? error.message : 'Unknown error occurred while sending message',
        timestamp: new Date()
      });
      
      // Auto-dismiss error after 10 seconds
      setTimeout(() => {
        setMessageSendError(null);
        setShowErrorDetails(false);
      }, 10000);
      
      // Show error to user but still add optimistic message with failed status
      const failedMessage: Message = {
        id: Date.now().toString(),
        content: newMessage,
        timestamp: new Date().toISOString(),
        isOutgoing: true,
        channel: 'email',
        status: 'failed',
        attachments: attachments,
        metadata: {
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      };
      
      setMessages(prev => [...prev, failedMessage]);
      setNewMessage('');
      setAttachments([]);
      
      // Update conversation list even for failed messages to trigger reordering
      setConversations(prev => 
        prev.map(conv => 
          conv.id === selectedConversation.id 
            ? {
                ...conv,
                lastMessage: failedMessage,
                messageCount: conv.messageCount + 1,
                updatedAt: failedMessage.timestamp
              }
            : conv
        )
      );
      
    } finally {
      setSendingMessage(false);
    }
  };

  // Real conversations loading with database connection
  const loadConversations = useCallback(async () => {
    console.log('🔄 Loading conversations from database...');
    setLoading(true);
    setError(null);
    
    try {
      // Use the existing conversation service with real API endpoint
      const { conversationService } = await import('../../services/conversationService');
      
      // Get conversations with current filters using the production API
      const apiFilters = {
        page: 1,
        limit: 50,
        ...(searchQuery && { search: searchQuery }),
        ...(filters.status !== 'all' && { status: filters.status })
      };
      
      console.log('📡 Calling conversations API with filters:', apiFilters);
      const result = await conversationService.getConversations(apiFilters);
      console.log('✅ Conversations loaded from database:', result);

      if (result && result.conversations && result.conversations.length > 0) {
        // Map the real API response to our conversation interface
        const conversations: Conversation[] = result.conversations.map((conv: any) => ({
          id: conv.id,
          contact: {
            id: conv.contact?.id || conv.contact_id || conv.id,
            name: conv.contact?.name || conv.contact_name || conv.contactName || 'Unknown Contact',
            email: conv.contact?.email || conv.contact_email || conv.contactEmail || 'unknown@example.com',
            company: conv.contact?.company || conv.contact_company || conv.contactCompany || conv.company,
            avatar: conv.contact?.avatar || conv.contact_avatar || conv.avatar
          },
          lastMessage: {
            id: conv.last_message_id || conv.lastMessageId || 'no-message',
            content: conv.last_message_content || 
                     (typeof conv.lastMessage === 'string' ? conv.lastMessage : 
                      typeof conv.lastMessage === 'object' && conv.lastMessage?.content ? conv.lastMessage.content :
                      'No messages yet'),
            timestamp: conv.last_message_at || conv.lastMessageAt || conv.updated_at || new Date().toISOString(),
            isOutgoing: conv.last_message_direction === 'outbound' || conv.lastMessageDirection === 'outbound',
            channel: conv.channel || 'email',
            status: conv.last_message_status || 'sent'
          },
          status: conv.status || 'active',
          priority: conv.priority || 'medium',
          campaign: conv.campaign || conv.campaign_id || conv.campaignId,
          messageCount: conv.message_count || conv.messageCount || conv.response_count || 0,
          unreadCount: conv.unread_count || conv.unreadCount || 0,
          labels: conv.tags || conv.labels || [],
          aiAnalysis: conv.ai_analysis ? {
            sentiment: conv.ai_analysis.sentiment || 'neutral',
            intent: conv.ai_analysis.intent || 'general',
            confidence: conv.ai_analysis.confidence || 0.5
          } : undefined,
          isStarred: conv.is_starred || conv.isStarred || false,
          updatedAt: conv.updated_at || conv.updatedAt || new Date().toISOString()
        }));
        
        console.log('✅ Mapped conversations from database:', conversations.length);
        setConversations(conversations);
        
        // Auto-select first conversation if none selected
        if (!selectedConversation && conversations.length > 0) {
          setSelectedConversation(conversations[0]);
        }
      } else {
        console.log('📭 No conversations found in database');
        setConversations([]);
      }
      
    } catch (error) {
      console.error('❌ Failed to load conversations from database:', error);
      setError(error instanceof Error ? error.message : 'Failed to load conversations');
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filters]);

  // Refresh function for manual refresh
  const handleRefresh = useCallback(async () => {
    if (refreshing) return;
    
    setRefreshing(true);
    console.log('🔄 Manual refresh triggered');
    
    try {
      await loadConversations();
      
      // Also refresh current conversation messages if one is selected
      if (selectedConversation?.id) {
        await loadMessages(selectedConversation.id);
      }
      
      console.log('✅ Manual refresh completed');
    } catch (error) {
      console.error('❌ Manual refresh failed:', error);
    } finally {
      setRefreshing(false);
    }
  }, [refreshing, loadConversations, loadMessages, selectedConversation?.id]);

  // Handle AI analysis completion
  const handleAIAnalysisComplete = useCallback((analysis: AIAnalysisResult) => {
    setAiAnalysis(analysis);
    console.log('✅ AI Analysis completed:', analysis);
  }, []);

  // Real-time event handlers
  const handleNewMessage = useCallback((conversationId: string, message: Message) => {
    console.log('📨 Real-time new message:', { conversationId, message });
    
    // If this is the currently selected conversation, add the message immediately
    if (selectedConversation?.id === conversationId) {
      setMessages(prev => [...prev, message]);
      
      // Auto-scroll to bottom like iMessage
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
    
    // Update the conversation in the sidebar with new last message and increment count
    setConversations(prev => prev.map(conv => {
      if (conv.id === conversationId) {
        return {
          ...conv,
          lastMessage: message,
          messageCount: conv.messageCount + 1,
          unreadCount: selectedConversation?.id === conversationId ? conv.unreadCount : conv.unreadCount + 1,
          updatedAt: message.timestamp
        };
      }
      return conv;
    }));
  }, [selectedConversation?.id]);

  const handleMessageStatusUpdate = useCallback((conversationId: string, messageId: string, status: Message['status']) => {
    console.log('📋 Real-time message status update:', { conversationId, messageId, status });
    
    // Update message status in current conversation if it's selected
    if (selectedConversation?.id === conversationId) {
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, status } : msg
      ));
    }
    
    // Update conversation's last message status if it's the most recent one
    setConversations(prev => prev.map(conv => {
      if (conv.id === conversationId && conv.lastMessage.id === messageId) {
        return {
          ...conv,
          lastMessage: { ...conv.lastMessage, status }
        };
      }
      return conv;
    }));
  }, [selectedConversation?.id]);

  const handleConversationUpdate = useCallback((updatedConversation: Partial<Conversation>) => {
    console.log('💬 Real-time conversation update:', updatedConversation);
    
    setConversations(prev => prev.map(conv => 
      conv.id === updatedConversation.id ? { ...conv, ...updatedConversation } : conv
    ));
    
    // If this is the currently selected conversation, update it
    if (selectedConversation?.id === updatedConversation.id) {
      setSelectedConversation(prev => prev ? { ...prev, ...updatedConversation } : prev);
    }
  }, [selectedConversation?.id]);

  const handleConversationListUpdate = useCallback(() => {
    console.log('🔄 Real-time conversation list update triggered');
    
    // Refresh the conversation list to get the latest data
    // This could be optimized to only fetch new/updated conversations
    loadConversations();
  }, [loadConversations]);

  // Real-time conversations hook (declared after handlers)
  const { connectionStatus, subscribe, unsubscribe } = useRealTimeConversations({
    onNewMessage: handleNewMessage,
    onMessageStatusUpdate: handleMessageStatusUpdate,
    onConversationUpdate: handleConversationUpdate,
    onConversationListUpdate: handleConversationListUpdate
  });

  // Handle automation configuration save
  const handleAutomationSave = useCallback(async (config: AutomationConfig, activateNow?: boolean) => {
    try {
      console.log('💾 Saving automation config:', { config, activateNow });
      
      // Add flags to indicate activation details
      const configWithStatus = {
        ...config,
        wasAlreadyEnabled: automationConfig?.isEnabled || false,
        activateNow: activateNow ?? false,
        activationType: config.activationType || 'immediate'
      };
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/automation/${selectedConversation?.id}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(configWithStatus)
      });

      if (!response.ok) {
        throw new Error(`Failed to save automation config: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setAutomationConfig(result.data);
        console.log('✅ Automation config saved successfully');
        
        // Show different messages based on activation type
        if (config.isEnabled) {
          if (config.activationType === 'immediate' && activateNow) {
            console.log('🚀 Automation activated immediately');
          } else if (config.activationType === 'on_response') {
            console.log('⏰ Automation will start on next response');
          }
        } else {
          console.log('💾 Configuration saved without activation');
        }
        
        // Refresh queued responses after configuration change
        refetchQueued();
        
        // TODO: Add toast notification system
        
      } else {
        throw new Error(result.error || 'Failed to save automation config');
      }
      
    } catch (error) {
      console.error('❌ Failed to save automation config:', error);
      
      // For development, still store locally as fallback
      setAutomationConfig(config);
      console.log('⚠️ Saved to local storage as fallback');
      
      // TODO: Show error message to user
    }
  }, [selectedConversation?.id, automationConfig?.isEnabled, refetchQueued]);

  // Handle queued response actions
  const handleCancelQueuedResponse = useCallback(async (executionId: string) => {
    try {
      console.log('❌ Cancelling queued response:', executionId);
      await automationService.cancelExecution(executionId);
      console.log('✅ Queued response cancelled successfully');
      refetchQueued();
    } catch (error) {
      console.error('❌ Failed to cancel queued response:', error);
    }
  }, [refetchQueued]);

  const handlePauseQueuedResponse = useCallback(async (executionId: string) => {
    try {
      console.log('⏸️ Pausing queued response:', executionId);
      const result = await automationService.pauseExecution(executionId);
      console.log('✅ Response paused successfully', { remainingMinutes: result.remainingDelayMinutes });
      refetchQueued();
    } catch (error) {
      console.error('❌ Failed to pause queued response:', error);
    }
  }, [refetchQueued]);

  const handleResumeQueuedResponse = useCallback(async (executionId: string) => {
    try {
      console.log('▶️ Resuming queued response:', executionId);
      const result = await automationService.resumeExecution(executionId);
      console.log('✅ Response resumed successfully', { scheduledFor: result.scheduledFor });
      refetchQueued();
    } catch (error) {
      console.error('❌ Failed to resume queued response:', error);
    }
  }, [refetchQueued]);

  const handleSendNowQueuedResponse = useCallback(async (executionId: string) => {
    try {
      console.log('⚡ Sending queued response immediately:', executionId);
      await automationService.sendNow(executionId);
      console.log('✅ Response sent immediately');
      refetchQueued();
    } catch (error) {
      console.error('❌ Failed to send response immediately:', error);
    }
  }, [refetchQueued]);

  const handleRescheduleQueuedResponse = useCallback(async (executionId: string, delayMinutes: number) => {
    try {
      console.log('📅 Rescheduling queued response:', executionId, { delayMinutes });
      const result = await automationService.rescheduleExecution(executionId, delayMinutes);
      console.log('✅ Response rescheduled successfully', { scheduledFor: result.scheduledFor });
      refetchQueued();
    } catch (error) {
      console.error('❌ Failed to reschedule response:', error);
    }
  }, [refetchQueued]);

  // File upload functionality
  const handleFileUpload = useCallback(async (files: FileList) => {
    if (!files || files.length === 0) return;
    
    setUploadingFiles(true);
    console.log('📎 Uploading files:', Array.from(files).map(f => f.name));
    
    try {
      const fileArray = Array.from(files);
      
      // Validate file types and sizes
      const maxFileSize = 10 * 1024 * 1024; // 10MB
      const allowedTypes = ['image/', 'application/pdf', 'text/', 'audio/', 'video/'];
      
      for (const file of fileArray) {
        if (file.size > maxFileSize) {
          throw new Error(`File "${file.name}" is too large. Maximum size is 10MB.`);
        }
        
        const isAllowedType = allowedTypes.some(type => file.type.startsWith(type));
        if (!isAllowedType) {
          throw new Error(`File type "${file.type}" is not supported.`);
        }
      }
      
      // Add files to attachments
      setAttachments(prev => [...prev, ...fileArray]);
      console.log('✅ Files added to attachments:', fileArray.length);
      
    } catch (error) {
      console.error('❌ File upload validation failed:', error);
      setError(error instanceof Error ? error.message : 'File upload failed');
      
      // Clear error after 5 seconds
      setTimeout(() => setError(null), 5000);
    } finally {
      setUploadingFiles(false);
    }
  }, []);

  // Remove attachment
  const handleRemoveAttachment = useCallback((index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    console.log('🗑️ Attachment removed at index:', index);
  }, []);

  // Handle file drop
  const handleFileDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  }, [handleFileUpload]);

  // Handle file input change
  const handleFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files);
    }
    // Reset input value to allow uploading the same file again
    event.target.value = '';
  }, [handleFileUpload]);

  // Enhanced filtering with all filter types
  const filteredConversations = useMemo(() => {
    const filtered = conversations.filter(conversation => {
      const searchTerm = searchQuery.toLowerCase();
      const matchesSearch = 
        conversation.contact.name.toLowerCase().includes(searchTerm) ||
        conversation.contact.email.toLowerCase().includes(searchTerm) ||
        conversation.contact.company?.toLowerCase().includes(searchTerm) ||
        conversation.lastMessage.content.toLowerCase().includes(searchTerm);
      
      const matchesStatus = filters.status === 'all' || conversation.status === filters.status;
      const matchesPriority = filters.priority === 'all' || conversation.priority === filters.priority;
      const matchesChannel = filters.channel === 'all' || conversation.lastMessage.channel === filters.channel;
      
      // Assignment filter - for now we'll simulate this, real implementation coming soon
      const matchesAssignment = filters.assignedTo === 'all' || 
        (filters.assignedTo === 'ai' && Math.random() > 0.5) || // Simulate AI assignment
        (filters.assignedTo === 'unassigned' && Math.random() > 0.7); // Simulate unassigned
      
      // Date range filter - for now we'll show all, real implementation coming soon
      const matchesDateRange = filters.dateRange === 'all' || true; // Coming soon
      
      return matchesSearch && matchesStatus && matchesPriority && matchesChannel && matchesAssignment && matchesDateRange;
    });

    // Sort conversations by last message timestamp (most recent first) - like iMessage
    const sorted = filtered.sort((a, b) => {
      const aTimestamp = new Date(a.lastMessage.timestamp || a.updatedAt).getTime();
      const bTimestamp = new Date(b.lastMessage.timestamp || b.updatedAt).getTime();
      return bTimestamp - aTimestamp; // Descending order (newest first)
    });
    
    console.log('🔄 Conversations sorted by timestamp:', 
      sorted.slice(0, 3).map(c => ({
        id: c.id,
        contact: c.contact.name,
        lastMessageTime: c.lastMessage.timestamp,
        updatedAt: c.updatedAt
      }))
    );
    
    return sorted;
  }, [conversations, searchQuery, filters]);

  // Enhanced stats calculation
  const stats: ConversationStats = useMemo(() => ({
    totalConversations: conversations.length,
    activeConversations: conversations.filter(c => ['active', 'priority', 'needs-response'].includes(c.status)).length,
    avgResponseTime: '2.3h',
    responseRate: Math.round((conversations.filter(c => c.messageCount > 1).length / conversations.length) * 100)
  }), [conversations]);

  // Effects with proper dependency management
  useEffect(() => {
    // Auto-setup demo authentication if no token exists
    autoSetupDemoAuth();
    console.log('🔄 useEffect: calling loadConversations on mount');
    loadConversations();
  }, []); // Only run on mount

  useEffect(() => {
    console.log('🔄 useEffect: filters or searchQuery changed', { filters, searchQuery });
    if (conversations.length > 0) {
      // Only reload if we already have conversations loaded
      loadConversations();
    }
  }, [filters, searchQuery, loadConversations]);

  useEffect(() => {
    console.log('🔄 useEffect: selectedConversation changed', selectedConversation?.id);
    if (selectedConversation?.id) {
      loadMessages(selectedConversation.id);
      loadAutomationConfig(selectedConversation.id);
      
      // Subscribe to real-time updates for this conversation
      subscribe(selectedConversation.id);
    }
    
    // Cleanup: unsubscribe from previous conversation when switching
    return () => {
      if (selectedConversation?.id) {
        unsubscribe(selectedConversation.id);
      }
    };
  }, [selectedConversation?.id, loadMessages, subscribe, unsubscribe]);

  // Load automation configuration for selected conversation
  const loadAutomationConfig = useCallback(async (conversationId: string) => {
    try {
      console.log('🔄 Loading automation config for conversation:', conversationId);
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/automation/${conversationId}`, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 404) {
        // No automation config exists, which is fine - this is expected for most conversations
        setAutomationConfig(null);
        console.log('📭 No automation config found for conversation (this is normal)');
        return;
      }

      if (!response.ok) {
        // Only log actual errors, not expected 404s
        console.warn(`⚠️ Failed to load automation config: ${response.status} ${response.statusText}`);
        setAutomationConfig(null);
        return;
      }

      const result = await response.json();
      
      if (result.success) {
        setAutomationConfig(result.data);
        console.log('✅ Automation config loaded:', result.data);
      } else {
        console.warn('⚠️ Failed to load automation config:', result.error);
        setAutomationConfig(null);
      }
      
    } catch (error) {
      // Only log unexpected errors, suppress network errors for 404s
      if (error instanceof Error && !error.message.includes('404')) {
        console.error('❌ Error loading automation config:', error);
      }
      setAutomationConfig(null);
    }
  }, []);

  // Connection status simulation
  useEffect(() => {
    const interval = setInterval(() => {
      setIsConnected(true); // Keep it simple - always connected for now
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Auto-scroll to bottom only when messages are initially loaded or when user is near bottom
  useEffect(() => {
    if (messagesEndRef.current && messages.length > 0) {
      // Only scroll if this is the initial load or user was already near bottom
      const messageContainer = messagesEndRef.current.closest('.messages-container');
      if (messageContainer) {
        const { scrollTop, scrollHeight, clientHeight } = messageContainer;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
        const isInitialLoad = messages.length <= 1;
        
        if (isInitialLoad || isNearBottom) {
          messagesEndRef.current.scrollIntoView({ behavior: 'instant' });
        }
      } else {
        // Fallback for initial load
        messagesEndRef.current.scrollIntoView({ behavior: 'instant' });
      }
    }
  }, [messages]);

  // Enhanced loading state
  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
        <div className="w-80 border-r bg-white dark:bg-gray-800 p-4 space-y-4">
          <div className="space-y-2">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
          <Skeleton className="h-10 w-full" />
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        </div>
        <div className="flex-1 p-4">
          <Skeleton className="h-full w-full" />
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
        {/* Enhanced Sidebar */}
        <div className="w-80 border-r bg-white dark:bg-gray-800 flex flex-col">
          {/* Header */}
          <div className="p-4 border-b bg-white dark:bg-gray-800 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Conversations
                </h1>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
                </Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="h-4 w-4" />
              </Button>
              </div>
            </div>

            <ConnectionStatus 
              isConnected={connectionStatus.connected} 
              connectionStatus={connectionStatus} 
            />

            {/* Enhanced Stats */}
            <div className="grid grid-cols-2 gap-2">
              <MetricCard
                title="Total"
                value={stats.totalConversations}
                icon={MessageSquare}
                trend="up"
                change="+12%"
              />
              <MetricCard
                title="Active"
                value={stats.activeConversations}
                icon={Users}
                trend="up"
                change="+5%"
              />
            </div>

            {/* Enhanced Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
              />
            </div>
          </div>

          {/* Advanced Filter Panel */}
          <FilterPanel
            filters={filters}
            onFiltersChange={setFilters}
            teamUsers={teamUsers}
            isExpanded={showAdvancedFilters}
            onToggle={() => setShowAdvancedFilters(!showAdvancedFilters)}
          />

          {/* Enhanced Conversation List */}
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-3">
              {error ? (
                <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                  <AlertDescription className="text-red-700 dark:text-red-300">
                    {error}
                  </AlertDescription>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={loadConversations}
                    className="mt-2"
                  >
                    Try Again
                  </Button>
                </Alert>
              ) : filteredConversations.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                    {searchQuery ? 'No conversations match your search' : 'No conversations yet'}
                  </p>
                  {searchQuery && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setSearchQuery('')}
                      className="text-blue-600"
                    >
                      Clear search
                    </Button>
                  )}
                </div>
              ) : (
                <AnimatePresence mode="popLayout">
                  {filteredConversations.map((conversation) => (
                    <motion.div
                      key={conversation.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      layout
                      transition={{ duration: 0.2 }}
                    >
                      <ConversationListItem
                        conversation={conversation}
                        isSelected={selectedConversation?.id === conversation.id}
                        onClick={() => setSelectedConversation(conversation)}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Enhanced Main Content */}
        <div className="flex-1 flex flex-col bg-white dark:bg-gray-800">
          {selectedConversation ? (
            <>
              {/* Enhanced Conversation Header with Campaign & Contact Info */}
              <div className="border-b bg-white dark:bg-gray-800">
                {/* Main Header */}
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar
                        src={selectedConversation.contact.avatar}
                        fallback={selectedConversation.contact.name
                          .split(' ')
                          .map(n => n.charAt(0))
                          .join('')
                          .substring(0, 2)
                          .toUpperCase()}
                        online={Math.random() > 0.5}
                        size="lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {selectedConversation.contact.name}
                          </h2>
                          {selectedConversation.isStarred && (
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                          <Mail className="h-3 w-3" />
                          <span>{selectedConversation.contact.email}</span>
                        </div>
                        {selectedConversation.contact.company && (
                          <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                            <Building2 className="h-3 w-3" />
                            <span>{selectedConversation.contact.company}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Badge className={cn("border", statusConfig[selectedConversation.status].className)}>
                        {statusConfig[selectedConversation.status].label}
                      </Badge>
                      
                      {/* Automation Status Badge */}
                      <AutomationBadge 
                        conversationId={selectedConversation.id}
                        showTimer={true}
                        className="border border-gray-200 dark:border-gray-700"
                      />
                      
                      {/* Real-time Status Indicator */}
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Badge className={cn(
                              "text-xs border",
                              connectionStatus.connected 
                                ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800"
                                : "bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800"
                            )}>
                              {connectionStatus.connected ? (
                                <Wifi className="h-3 w-3 mr-1" />
                              ) : (
                                <WifiOff className="h-3 w-3 mr-1" />
                              )}
                              Live
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="text-xs">
                              <div><strong>Real-time Updates:</strong> {connectionStatus.connected ? 'Connected' : 'Disconnected'}</div>
                              {connectionStatus.lastPing && (
                                <div><strong>Latency:</strong> {connectionStatus.lastPing}ms</div>
                              )}
                              {connectionStatus.reconnectAttempts > 0 && (
                                <div><strong>Reconnect attempts:</strong> {connectionStatus.reconnectAttempts}</div>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => setShowAIAnalysis(!showAIAnalysis)}
                          className={cn(
                            showAIAnalysis && "bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400"
                          )}
                          title="AI Analysis (Cmd+Shift+A)"
                        >
                          <Brain className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => setShowAutomation(!showAutomation)}
                          className={cn(
                            showAutomation && "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400"
                          )}
                          title="Automated Responses (Cmd+Shift+R)"
                        >
                          <Sparkles className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon">
                          <Star className={cn(
                            "h-4 w-4",
                            selectedConversation.isStarred && "fill-yellow-400 text-yellow-400"
                          )} />
                        </Button>
                        <Button variant="ghost" size="icon">
                          <Archive className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Campaign & Workflow Info Section */}
                <div className="px-4 pb-3 border-b border-gray-100 dark:border-gray-700">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4">
                      {selectedConversation.campaign && (
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                          <MessageSquare className="h-3 w-3" />
                          <span className="font-medium">Campaign:</span>
                          <span>{selectedConversation.campaign}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <Bot className="h-3 w-3" />
                        <span className="font-medium">Source:</span>
                        <span>Workflow Automation</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500">
                      <span>Messages: {selectedConversation.messageCount}</span>
                      <span>Priority: {selectedConversation.priority}</span>
                      <span>Channel: {selectedConversation.lastMessage.channel}</span>
                    </div>
                  </div>
                  
                  {/* Contact Details - Coming Soon Section */}
                  <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <span className="text-gray-500 dark:text-gray-500">Phone: </span>
                        <span className="text-gray-400">Coming soon</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-500">LinkedIn: </span>
                        <span className="text-gray-400">Coming soon</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-500">Lead Score: </span>
                        <span className="text-gray-400">Coming soon</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-500">Last Activity: </span>
                        <span className="text-gray-400">Coming soon</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Enhanced Messages */}
              <ScrollArea className="flex-1 p-4 bg-gray-50 dark:bg-gray-900">
                <div className="space-y-4 max-w-4xl mx-auto">
                  {/* Message Send Error Banner */}
                  {messageSendError && (
                    <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                                {messageSendError.message}
                              </h4>
                              <span className="text-xs text-red-600 dark:text-red-400">
                                {messageSendError.timestamp.toLocaleTimeString()}
                              </span>
                            </div>
                            <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                              Your message couldn't be sent. Click "Show Details" to see what went wrong.
                            </p>
                            <div className="flex gap-2 mt-3">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowErrorDetails(!showErrorDetails)}
                                className="border-red-300 text-red-700 hover:bg-red-100 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900"
                              >
                                {showErrorDetails ? 'Hide Details' : 'Show Details'}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  // Retry sending the last failed message
                                  const lastFailedMessage = messages.find(m => m.status === 'failed');
                                  if (lastFailedMessage) {
                                    setNewMessage(lastFailedMessage.content);
                                    setMessageSendError(null);
                                  }
                                }}
                                className="border-red-300 text-red-700 hover:bg-red-100 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900"
                              >
                                Retry
                              </Button>
                            </div>
                            {showErrorDetails && (
                              <div className="mt-3 p-3 bg-red-100 dark:bg-red-900/50 rounded border border-red-200 dark:border-red-800">
                                <p className="text-xs font-mono text-red-800 dark:text-red-200 break-all">
                                  <strong>Technical Details:</strong><br />
                                  {messageSendError.details}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setMessageSendError(null);
                            setShowErrorDetails(false);
                          }}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {messages.map((message, index) => (
                    <MessageBubble 
                      key={message.id} 
                      message={message} 
                      contact={selectedConversation.contact}
                      isLast={index === messages.length - 1 && queuedResponses.length === 0}
                      onUserClick={(user) => {
                        setSelectedUser(user);
                        setShowUserProfile(true);
                      }}
                      onInfoClick={(messageInfo) => {
                        setSelectedMessageInfo(messageInfo);
                        setShowMessageInfo(true);
                      }}
                    />
                  ))}

                  {/* Queued Automated Responses */}
                  {(() => {
                    console.log('🔍 Rendering queued responses:', {
                      count: queuedResponses.length,
                      responses: queuedResponses.map(r => ({
                        id: r.id,
                        executionStatus: r.executionStatus,
                        hasOnSendNow: !!handleSendNowQueuedResponse
                      }))
                    });
                    return queuedResponses.map((queuedResponse, index) => (
                      <QueuedResponseBubble
                        key={queuedResponse.id}
                        queuedResponse={queuedResponse}
                        onCancel={handleCancelQueuedResponse}
                        onPause={handlePauseQueuedResponse}
                        onResume={handleResumeQueuedResponse}
                        onSendNow={handleSendNowQueuedResponse}
                        onReschedule={handleRescheduleQueuedResponse}
                      />
                    ));
                  })()}

                  {sendingMessage && (
                    <div className="flex justify-end">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        Sending...
                      </div>
                    </div>
                  )}
                  
                  {/* Auto-scroll target - like iMessage */}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Enhanced Message Input with File Upload */}
              <div className="border-t bg-white dark:bg-gray-800">
                {/* Attachments Preview */}
                {attachments.length > 0 && (
                  <div className="px-4 pt-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="max-w-4xl mx-auto">
                      <div className="flex items-center gap-2 mb-2">
                        <Paperclip className="h-4 w-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {attachments.length} attachment{attachments.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {attachments.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm"
                          >
                            <FileText className="h-4 w-4 text-gray-500" />
                            <span className="text-gray-700 dark:text-gray-300 truncate max-w-[200px]">
                              {file.name}
                            </span>
                            <span className="text-xs text-gray-500">
                              ({(file.size / 1024 / 1024).toFixed(1)}MB)
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-4 w-4 text-gray-500 hover:text-red-500"
                              onClick={() => handleRemoveAttachment(index)}
                            >
                              ×
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Message Input Area */}
                <div 
                  className="p-4"
                  onDrop={handleFileDrop}
                  onDragOver={(e) => e.preventDefault()}
                  onDragEnter={(e) => e.preventDefault()}
                >
                  <div className="flex gap-3 max-w-4xl mx-auto">
                    <div className="flex-1 relative">
                      <AutoExpandingTextarea
                        placeholder="Type your message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        minRows={1}
                        maxRows={8}
                        maxHeight={200} // Like iMessage, cap at reasonable height
                        className="border-gray-200 dark:border-gray-700 pr-20"
                        disabled={sendingMessage || uploadingFiles}
                      />
                      <div className="absolute bottom-3 right-3 flex gap-1">
                        <input
                          type="file"
                          multiple
                          onChange={handleFileInputChange}
                          className="hidden"
                          id="file-upload"
                          accept="image/*,application/pdf,text/*,audio/*,video/*"
                        />
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7 hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => document.getElementById('file-upload')?.click()}
                          disabled={uploadingFiles}
                        >
                          {uploadingFiles ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Paperclip className="h-4 w-4" />
                          )}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <Smile className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <Button 
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim() || sendingMessage || uploadingFiles}
                      className="self-end bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {sendingMessage ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  
                  {/* Upload Status */}
                  {uploadingFiles && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-4xl mx-auto">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Processing files...</span>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
              <div className="text-center">
                <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Select a conversation
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Choose a conversation from the sidebar to start messaging
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Enhanced AI Analysis Sidebar with Response Generation */}
        <AIAnalysisSidebar
          isOpen={showAIAnalysis}
          onClose={() => setShowAIAnalysis(false)}
          conversationId={selectedConversation?.id || ''}
          conversationData={selectedConversation}
          messages={messages}
          onAnalysisComplete={handleAIAnalysisComplete}
          onResponseGenerated={(response) => {
            console.log('🤖 AI Response Generated:', response);
            // Auto-populate the message input with the generated response
            setNewMessage(response.content);
          }}
        />

        {/* User Profile Modal */}
        <UserProfileModal
          user={selectedUser}
          isOpen={showUserProfile}
          onClose={() => {
            setShowUserProfile(false);
            setSelectedUser(null);
          }}
        />

        {/* Message Info Modal */}
        <MessageInfoModal
          messageInfo={selectedMessageInfo}
          isOpen={showMessageInfo}
          onClose={() => {
            setShowMessageInfo(false);
            setSelectedMessageInfo(null);
          }}
        />

        {/* Automation Modal */}
        <AutomationModal
          conversationId={selectedConversation?.id || ''}
          isOpen={showAutomation}
          onClose={() => setShowAutomation(false)}
          onSave={handleAutomationSave}
          existingConfig={automationConfig}
        />
      </div>
    </TooltipProvider>
  );
};

export default ImprovedConversationsPage; 